import { useState, useMemo } from 'react';
import type { GetStaticProps, NextPage } from 'next';
import {
  Box,
  Container,
  Heading,
  Text,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  LinkBox,
  VStack,
} from '@chakra-ui/react';
import { format } from 'date-fns';

import { BaseLayout } from 'components/common';
import SeoHead from 'components/common/SeoHead';
import BlogImage from 'components/blog/BlogImage';
import {
  matchesAudience,
  type AudienceFilter,
  type BlogPostListItem,
} from 'API/datocms';
import { blogSnapshot } from 'utils/blog-snapshot';

/* ── Constants ───────────────────────────────────────────────────── */

const BRAND = '#079ba9';
const INK = '#1e3d60';
const MUTED = '#62778f';
const TERTIARY = '#94a3b8';
const BORDER = '#e7eaee';
const HOVER_BORDER = '#becad7';

const formatDate = (value: string | null) =>
  value ? format(new Date(value), 'MMM d, yyyy') : '';

/** Decorative gradient + emoji for the card thumbnail, cycled by index. */
const THUMBS = [
  {
    bg: 'linear-gradient(135deg, #e0f4f5, #5dc4c4)',
    emoji: '📚',
    color: '#06707d',
  },
  {
    bg: 'linear-gradient(135deg, #079ba9, #06707d)',
    emoji: '✉️',
    color: '#fff',
  },
  {
    bg: 'linear-gradient(135deg, #faeeda, #fac775)',
    emoji: '🕐',
    color: '#854F0B',
  },
  {
    bg: 'linear-gradient(135deg, #e6f1fb, #b5d4f4)',
    emoji: '📊',
    color: '#185FA5',
  },
  {
    bg: 'linear-gradient(135deg, #eeedfe, #afa9ec)',
    emoji: '🧠',
    color: '#3C3489',
  },
  {
    bg: 'linear-gradient(135deg, #faece7, #f0997b)',
    emoji: '💬',
    color: '#993C1D',
  },
];
const thumbFor = (i: number) => THUMBS[i % THUMBS.length];

/** Map audience field to tag label + colours, or null when not audience-specific
 *  (untagged or "Both") so we never mislabel a post. */
const audienceTag = (
  audience: string | null,
): { label: string; bg: string; color: string } | null => {
  const a = (audience ?? '').toLowerCase();
  if (a === 'tutors') return { label: 'Tutor', bg: '#e6f1fb', color: '#185fa5' };
  if (a === 'students') return { label: 'Student', bg: '#e0f4f5', color: '#06707d' };
  return null;
};

/* ── Sub-components ──────────────────────────────────────────────── */

const CardThumb = ({
  post,
  index,
}: {
  post: BlogPostListItem;
  index: number;
}) => {
  const thumb = thumbFor(index);
  return (
    <Box
      w="140px"
      minH="120px"
      flexShrink={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      fontSize="36px"
      position="relative"
      overflow="hidden"
      bg={thumb.bg}
      sx={{
        '> *': {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        },
      }}
    >
      {post.externalImageUrl || post.coverImage ? (
        <BlogImage post={post} layout="fill" objectFit="cover" />
      ) : (
        <Box
          as="span"
          aria-hidden
          sx={{ position: 'relative !important' }}
          color={thumb.color}
        >
          {thumb.emoji}
        </Box>
      )}
    </Box>
  );
};

const BlogCard = ({
  post,
  index,
}: {
  post: BlogPostListItem;
  index: number;
}) => {
  const tag = audienceTag(post.audience);

  return (
    <LinkBox
      as="a"
      href={`/blog/${post.slug}`}
      display="flex"
      bg="white"
      border={`0.5px solid ${BORDER}`}
      borderRadius="14px"
      overflow="hidden"
      transition="border-color 0.15s"
      _hover={{ borderColor: HOVER_BORDER }}
    >
      <CardThumb post={post} index={index} />
      <Flex p="16px 20px" direction="column" justify="center" flex="1" minW={0}>
        <Flex align="center" gap={2} mb="6px" flexWrap="wrap">
          {tag && (
            <Box
              as="span"
              display="inline-flex"
              alignItems="center"
              gap="4px"
              fontSize="11px"
              fontWeight={500}
              px="8px"
              py="3px"
              borderRadius="999px"
              bg={tag.bg}
              color={tag.color}
            >
              {tag.label}
            </Box>
          )}
          <Text fontSize="12px" color={TERTIARY} m={0}>
            {formatDate(post.firstPublishedAt)}
          </Text>
        </Flex>

        <Text
          fontSize="15px"
          fontWeight={500}
          color={INK}
          m="0 0 5px"
          lineHeight="1.4"
        >
          {post.title || 'Untitled'}
        </Text>

        {post.excerpt && (
          <Text
            fontSize="13px"
            color={MUTED}
            lineHeight="1.6"
            m="0 0 10px"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {post.excerpt}
          </Text>
        )}

        <Box
          as="span"
          color={BRAND}
          fontSize="13px"
          fontWeight={500}
          display="inline-flex"
          alignItems="center"
          gap="4px"
          mt="auto"
        >
          Read article <Box as="span">→</Box>
        </Box>
      </Flex>
    </LinkBox>
  );
};

/* ── Tabs ────────────────────────────────────────────────────────── */

type TabKey = 'all' | AudienceFilter;

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'students', label: 'For students' },
  { key: 'tutors', label: 'For tutors' },
];

/* ── Page ────────────────────────────────────────────────────────── */

type BlogIndexProps = {
  posts: BlogPostListItem[];
};

const BlogIndex: NextPage<BlogIndexProps> = ({ posts }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [tab, setTab] = useState<TabKey>('all');

  const normalizedQuery = searchQuery
    .trim()
    .split(/\s+/)
    .join(' ')
    .toLowerCase();

  const filtered = useMemo(() => {
    return posts.filter((post) => {
      const matchTab = tab === 'all' || matchesAudience(post.audience, tab);
      const matchSearch =
        !normalizedQuery ||
        (post.title && post.title.toLowerCase().includes(normalizedQuery)) ||
        (post.excerpt &&
          post.excerpt.toLowerCase().includes(normalizedQuery)) ||
        (post.category &&
          post.category.toLowerCase().includes(normalizedQuery));
      return matchTab && matchSearch;
    });
  }, [posts, tab, normalizedQuery]);

  const resultLabel =
    filtered.length === 1 ? '1 article' : `${filtered.length} articles`;

  return (
    <>
      <SeoHead
        title="Blog | Vital Educators"
        description="Career advice, exam tips, and educational insights from the Vital Educators blog."
        path="/blog"
      />
      <BaseLayout>
        <Box
          as="main"
          bg="#f9fafb"
          fontFamily="'Kumbh Sans', sans-serif"
          pb={16}
        >
          <Container
            maxW="860px"
            pt={{ base: 10, md: 12 }}
            pb={{ base: 6, md: 8 }}
          >
            {/* Header */}
            <Text
              fontSize="11px"
              fontWeight={500}
              letterSpacing="0.1em"
              textTransform="uppercase"
              color={BRAND}
              mb="10px"
            >
              The Vital Educators Blog
            </Text>
            <Heading
              as="h1"
              fontSize={{ base: '24px', md: '28px' }}
              fontWeight={500}
              color={INK}
              mb="8px"
              lineHeight="1.25"
            >
              Insights, tips &amp; resources
            </Heading>
            <Text
              fontSize="14px"
              color={MUTED}
              lineHeight="1.6"
              maxW="480px"
              mb="24px"
            >
              Career advice, exam strategies, and educational insights to help
              students learn with confidence.
            </Text>

            {/* Controls: search + tabs */}
            <Flex align="center" gap={3} mb="20px" flexWrap="wrap">
              <InputGroup flex="1" minW="200px" size="md">
                <InputLeftElement pointerEvents="none">
                  <Icon viewBox="0 0 24 24" color={TERTIARY} boxSize={4}>
                    <path
                      fill="currentColor"
                      d="M21.71 20.29l-5.4-5.39A7.92 7.92 0 0018 10a8 8 0 10-8 8 7.92 7.92 0 004.9-1.69l5.39 5.4a1 1 0 001.42-1.42zM4 10a6 6 0 116 6 6 6 0 01-6-6z"
                    />
                  </Icon>
                </InputLeftElement>
                <Input
                  placeholder="Search by title, topic, or keyword…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  bg="white"
                  border={`0.5px solid ${BORDER}`}
                  borderRadius="14px"
                  _hover={{ borderColor: HOVER_BORDER }}
                  _focus={{
                    borderColor: BRAND,
                    boxShadow: `0 0 0 1px ${BRAND}`,
                  }}
                  _placeholder={{ color: TERTIARY }}
                  fontSize="14px"
                />
              </InputGroup>

              <Flex gap="6px">
                {TABS.map((t) => (
                  <Box
                    as="button"
                    key={t.key}
                    type="button"
                    fontSize="13px"
                    fontWeight={500}
                    fontFamily="inherit"
                    px="16px"
                    py="7px"
                    borderRadius="999px"
                    border="0.5px solid"
                    borderColor={tab === t.key ? BRAND : BORDER}
                    bg={tab === t.key ? BRAND : 'transparent'}
                    color={tab === t.key ? '#fff' : MUTED}
                    cursor="pointer"
                    whiteSpace="nowrap"
                    transition="background 0.15s, color 0.15s, border-color 0.15s"
                    _hover={tab !== t.key ? { bg: '#f1f4f7' } : undefined}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                  </Box>
                ))}
              </Flex>
            </Flex>

            {/* Results count */}
            <Text fontSize="12px" color={TERTIARY} mb="16px">
              {resultLabel}
            </Text>

            {/* Card list */}
            {filtered.length > 0 ? (
              <VStack spacing="12px" align="stretch">
                {filtered.map((post, i) => (
                  <BlogCard key={post.id} post={post} index={i} />
                ))}
              </VStack>
            ) : (
              <Box
                textAlign="center"
                py="3rem"
                color={TERTIARY}
                fontSize="14px"
              >
                <Box fontSize="24px" mb="8px">
                  🔍
                </Box>
                No articles match your search.
                <br />
                <Box
                  as="button"
                  type="button"
                  color={BRAND}
                  fontWeight={500}
                  bg="none"
                  border="none"
                  fontFamily="inherit"
                  fontSize="inherit"
                  cursor="pointer"
                  textDecoration="underline"
                  onClick={() => {
                    setSearchQuery('');
                    setTab('all');
                  }}
                >
                  Clear filters
                </Box>
              </Box>
            )}
          </Container>
        </Box>
      </BaseLayout>
    </>
  );
};

export const getStaticProps: GetStaticProps<BlogIndexProps> = async () => ({
  props: { posts: blogSnapshot.posts },
});

export default BlogIndex;
